import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { processTemplatesService } from '../services/processTemplates.service';
import type { ProcessTemplateFilters, ProcessTemplateInput } from '../types/processTemplate.types';

export function useProcessTemplates(companyId?: string, filters: ProcessTemplateFilters = {}) {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const activeCompanyId = companyId || profile?.company_id || '';
  const userId = profile?.id || '';
  const queryKey = ['estimates', 'process-templates', activeCompanyId, filters];

  const query = useQuery({
    queryKey,
    queryFn: () => processTemplatesService.list(activeCompanyId, filters),
    enabled: !!activeCompanyId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['estimates', 'process-templates'] });

  const createMutation = useMutation({
    mutationFn: (input: ProcessTemplateInput) => processTemplatesService.create(userId, input),
    onSuccess: () => {
      invalidate();
      toast.success('Đã tạo quy trình mẫu');
    },
    onError: (error: any) => toast.error(error?.message || 'Không tạo được quy trình mẫu'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ProcessTemplateInput> }) =>
      processTemplatesService.update(userId, id, activeCompanyId, updates),
    onSuccess: () => {
      invalidate();
      toast.success('Đã cập nhật quy trình mẫu');
    },
    onError: (error: any) => toast.error(error?.message || 'Không cập nhật được quy trình mẫu'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => processTemplatesService.delete(userId, id, activeCompanyId),
    onSuccess: () => {
      invalidate();
      toast.success('Đã xóa quy trình mẫu');
    },
    onError: (error: any) => toast.error(error?.message || 'Không xóa được quy trình mẫu'),
  });

  return {
    templates: query.data || [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    createTemplate: createMutation.mutateAsync,
    updateTemplate: updateMutation.mutateAsync,
    deleteTemplate: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
